import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { EventService } from '@base/services/private/event.service';
import { GlobalService } from '@base/services/private/global.service';
import { SessionService } from '@base/services/private/session.service';
import { AuthService } from '@core/services/auth.service';
import { environment } from 'environments/environment';
import { finalize, tap } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

/**
 * HTTP interceptor that conditionally adds a custom `hid` header to requests.
 *
 * Adds the `hid` value from session storage if an event exists and the user is valid.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next handler in the interceptor chain.
 * @returns Observable of the HTTP event stream, possibly with a modified request.
 *
 * @remarks
 * Injects `SessionService`, `AuthService`, and `EventService` to determine conditions.
 */
export const httpRequestInterceptor: HttpInterceptorFn = (req, next) => {
  const globalSrv = inject(GlobalService);
  const sessionSrv = inject(SessionService);
  const hid = sessionSrv.getItem('use') as string;
  const authSrv = inject(AuthService);
  const eventSrv = inject(EventService);
  const cookieSrv = inject(CookieService);
  const eventDir = eventSrv.data?.name; // whether from branded domain or non branded still need event dir on hit api
  const isDocumentHTML = req.url === `${environment.apiBaseUrl}/${eventDir}/`; // to delete hid on http header request
  const customSession = cookieSrv.get('custom-session');
  let isWithCredentials = false;

  globalSrv.setHttpRequestState(true);

  let modifiedReq = req.clone({
    withCredentials: isWithCredentials,
  });

  if (eventDir && !req.url.includes('assets')) {
    const updatedUrl = req.url.replace(/\/event-name/, `/${eventDir}`);
    modifiedReq = modifiedReq.clone({ url: updatedUrl });
  }

  if (eventDir && authSrv.isValidUser() && !req.url.includes('assets') && !isDocumentHTML) {
    modifiedReq = modifiedReq.clone({
      setHeaders: {
        hid: hid?.toString() ?? '',
      },
    });
  }

  if (customSession !== null && customSession !== '') {
    modifiedReq = modifiedReq.clone({
      setHeaders: {
        'x-session-key': `${customSession}`,
      },
    });
  }

  return next(modifiedReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const sessionKey = event.headers.get('x-session-key'); // use this to resolve error cross origin on cookies
        const expirySessionKey = event.headers.get('x-session-expiry'); // in ms

        if (sessionKey && expirySessionKey) {
          const expires = new Date(Number(expirySessionKey)); // convert to date

          cookieSrv.set('custom-session', sessionKey, {
            path: `/`,
            secure: true,
            sameSite: 'Lax',
            expires: expires,
          });
          isWithCredentials = false;
          return;
        }

        if (!sessionKey) {
          isWithCredentials = true;
        }
      }
    }),
    finalize(() => {
      globalSrv.setHttpRequestState(false);
    })
  );
};
